/*
 * Maison Panoramique : calculs du configurateur (aucune dépendance, aucun accès au DOM).
 * Chargé dans le navigateur (window.MPPricing) et dans Node pour les tests (module.exports).
 *
 * Règles (contrat de construction du 21/10/2026) :
 * - prix = surface du mur facturée × prix au m² de la matière ;
 * - surface facturée arrondie à l'unité de vente supérieure (0,1 m² par défaut) ;
 * - minimum de commande : un mur plus petit est facturé au minimum (290 €) ;
 * - lés égaux de 130 cm au plus (règle de l'imprimeur, 28/10) : n = plafond((largeur + 10) / 130),
 *   largeur d'un lé = (largeur + 10) / n, les 10 cm étant la marge de coupe de 5 cm de chaque côté ;
 * - tous les montants sont en centimes entiers (aucun flottant dans les prix).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MPPricing = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var MATERIAL_CODES = ['INT', 'PRE', 'ADH'];
  var MAX_STRIP_CM = 130; // largeur maximale d'un lé chez l'imprimeur
  var CUT_MARGIN_CM = 5; // marge de coupe ajoutée de chaque côté du mur, à nos frais

  function toInt(value) {
    if (value === '' || value === null || value === undefined) return NaN;
    var n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : NaN;
  }

  /** Saisie d'un nombre entier de centimètres : « 300 » oui ; « 300,5 », « 300.5 », « 3e2 » non. */
  function isIntegerInput(value) {
    if (typeof value === 'number') return Number.isInteger(value);
    return /^\s*\d+\s*$/.test(String(value == null ? '' : value));
  }

  /** Vérifie les dimensions saisies. Renvoie la liste des erreurs (vide si tout va bien). */
  function validateDimensions(widthCm, heightCm, limits) {
    var l = limits || {};
    var minW = l.minWidthCm || 40;
    var maxW = l.maxWidthCm || 1500;
    var minH = l.minHeightCm || 40;
    var maxH = l.maxHeightCm || 400;
    var errors = [];
    var w = toInt(widthCm);
    var h = toInt(heightCm);
    if (!Number.isFinite(w) && (widthCm === '' || widthCm == null)) errors.push({ field: 'width', code: 'missing' });
    else if (!isIntegerInput(widthCm)) errors.push({ field: 'width', code: 'not_integer' });
    else if (w < minW) errors.push({ field: 'width', code: 'too_small', limit: minW });
    else if (w > maxW) errors.push({ field: 'width', code: 'too_large', limit: maxW });
    if (!Number.isFinite(h) && (heightCm === '' || heightCm == null)) errors.push({ field: 'height', code: 'missing' });
    else if (!isIntegerInput(heightCm)) errors.push({ field: 'height', code: 'not_integer' });
    else if (h < minH) errors.push({ field: 'height', code: 'too_small', limit: minH });
    else if (h > maxH) errors.push({ field: 'height', code: 'too_large', limit: maxH });
    return errors;
  }

  /**
   * Lés égaux (règle de l'imprimeur) : la largeur commandée (mur + 5 cm de marge de coupe de chaque côté) est
   * partagée en n lés de même largeur, de 130 cm au plus (330 cm → 340 cm commandés → 3 lés de 113,3 cm).
   */
  function equalStripCount(widthCm) {
    var w = toInt(widthCm);
    if (!(w > 0)) return 0;
    return Math.ceil((w + 2 * CUT_MARGIN_CM) / MAX_STRIP_CM);
  }

  /**
   * Plan des lés. Largeurs au millimètre : si la division ne tombe pas juste, les premiers lés prennent 1 mm de plus
   * (écart invisible, signalé sur le plan coté). Les lés couvrent la largeur commandée : le premier et le dernier
   * débordent du mur de la marge de coupe (marginCm).
   */
  function equalStrips(widthCm, heightCm) {
    var w = toInt(widthCm);
    var h = toInt(heightCm);
    if (!(w > 0) || !(h > 0)) return null;
    var n = equalStripCount(w);
    var orderedCm = w + 2 * CUT_MARGIN_CM;
    var totalMm = orderedCm * 10;
    var baseMm = Math.floor(totalMm / n);
    var extra = totalMm - baseMm * n;
    var list = [];
    for (var i = 0; i < n; i++) list.push({ number: i + 1, widthCm: (baseMm + (i < extra ? 1 : 0)) / 10, heightCm: h });
    return {
      count: n,
      equal: true,
      stripWidthCm: totalMm / n / 10,
      maxStripCm: MAX_STRIP_CM,
      marginCm: CUT_MARGIN_CM,
      orderedWidthCm: orderedCm,
      heightCm: h,
      lastWidthCm: list[n - 1].widthCm,
      lastIsPartial: false,
      list: list,
    };
  }

  /** Plan des lés : toujours des lés égaux (les largeurs standard 48, 66, 72 et 102 cm n'existent plus). */
  function stripPlan(widthCm, heightCm) {
    return equalStrips(widthCm, heightCm);
  }

  /**
   * Devis d'un mur.
   * opts.unitCm2 : unité de vente de la surface (1000 = 0,1 m², 100 = 1 dm²).
   */
  function quote(opts) {
    var w = toInt(opts.widthCm);
    var h = toInt(opts.heightCm);
    var rateCents = toInt(opts.rateEurPerM2 * 100);
    var minimumCents = toInt((opts.minimumEur == null ? 290 : opts.minimumEur) * 100);
    var unitCm2 = toInt(opts.unitCm2 || 1000);
    if (!(w > 0) || !(h > 0) || !(rateCents > 0) || !(unitCm2 > 0)) return null;

    // lé de rechange : surface d'un lé de plus (même hauteur), comptée par la même règle
    var extraCm2 = Math.max(0, toInt(opts.extraCm2) || 0);
    var surfaceCm2 = w * h + extraCm2; // entier exact
    var billedUnits = Math.ceil(surfaceCm2 / unitCm2);
    // prix d'une unité : rateCents (pour 10 000 cm²) × unitCm2 / 10 000
    var unitPriceCents = (rateCents * unitCm2) / 10000;
    var surfaceCents = Math.round(billedUnits * unitPriceCents);
    var minimumApplied = surfaceCents < minimumCents;
    var totalCents = minimumApplied ? minimumCents : surfaceCents;

    return {
      widthCm: w,
      heightCm: h,
      surfaceCm2: surfaceCm2,
      surfaceM2: surfaceCm2 / 10000,
      wallM2: (w * h) / 10000,
      extraCm2: extraCm2,
      unitCm2: unitCm2,
      billedUnits: billedUnits,
      billedM2: (billedUnits * unitCm2) / 10000,
      rateCents: rateCents,
      unitPriceCents: unitPriceCents,
      surfaceCents: surfaceCents,
      minimumCents: minimumCents,
      minimumApplied: minimumApplied,
      totalCents: totalCents,
      strips: opts.stripWidthCm ? stripPlan(w, h) : null,
    };
  }

  /** Surface au-dessous de laquelle le minimum s'applique (en m², pour l'affichage). */
  function minimumSurfaceM2(rateEurPerM2, minimumEur) {
    return Math.round(((minimumEur == null ? 290 : minimumEur) / rateEurPerM2) * 100) / 100;
  }

  var SKU_RE = /^MPSM-(INT|PRE|ADH)-(MIN|DM2|\d+)$/;

  function parseSku(sku) {
    var m = SKU_RE.exec(String(sku || '').trim().toUpperCase());
    if (!m) return null;
    if (m[2] === 'MIN') return { material: m[1], kind: 'minimum' };
    if (m[2] === 'DM2') return { material: m[1], kind: 'dm2' };
    // palier : surface facturée en dm² (750 = 7,50 m²)
    return { material: m[1], kind: 'step', dm2: parseInt(m[2], 10) };
  }

  /**
   * Mode de vente disponible dans le catalogue pour une matière :
   * - 'ladder' : une variante par palier de surface, SKU = surface en dm² (MPSM-INT-750 = 7,50 m²)
   *              + forfait minimum (MPSM-INT-MIN) ; le pas du palier est un réglage (10 dm² = 0,1 m²) ;
   * - 'dm2'    : une variante au dm², vendue en quantité (SKU MPSM-INT-DM2) + forfait minimum ;
   * - 'none'   : produit technique absent ou incomplet.
   */
  function detectMode(variants, material) {
    var hasStep = false;
    var hasDm2 = false;
    (variants || []).forEach(function (v) {
      var p = parseSku(v.sku);
      if (!p || p.material !== material) return;
      if (p.kind === 'step') hasStep = true;
      if (p.kind === 'dm2') hasDm2 = true;
    });
    if (hasStep) return 'ladder';
    if (hasDm2) return 'dm2';
    return 'none';
  }

  /** Unité de vente en cm² : 1 dm² en mode quantité, le pas du palier sinon (10 dm² par défaut). */
  function unitForMode(mode, stepDm2) {
    if (mode === 'dm2') return 100;
    return (toInt(stepDm2) > 0 ? toInt(stepDm2) : 10) * 100;
  }

  /**
   * Lignes de panier qui correspondent EXACTEMENT au devis.
   * Garde-fou : si le prix de la variante trouvée diffère du prix affiché, on refuse.
   */
  function resolveCartLine(q, material, variants, mode) {
    if (!q) return { ok: false, reason: 'no_quote' };
    var list = (variants || []).map(function (v) {
      return { id: v.id, price: toInt(v.price), available: v.available !== false, parsed: parseSku(v.sku) };
    }).filter(function (v) {
      return v.parsed && v.parsed.material === material;
    });

    function pick(pred) {
      for (var i = 0; i < list.length; i++) if (pred(list[i])) return list[i];
      return null;
    }

    if (q.minimumApplied) {
      var min = pick(function (v) { return v.parsed.kind === 'minimum'; });
      if (!min) return { ok: false, reason: 'missing_variant' };
      if (!min.available) return { ok: false, reason: 'unavailable' };
      if (min.price !== q.totalCents) return { ok: false, reason: 'price_mismatch', expected: q.totalCents, found: min.price };
      return { ok: true, id: min.id, quantity: 1, lineCents: min.price };
    }

    if (mode === 'ladder') {
      var billedDm2 = (q.billedUnits * q.unitCm2) / 100;
      var step = pick(function (v) { return v.parsed.kind === 'step' && v.parsed.dm2 === billedDm2; });
      if (!step) return { ok: false, reason: 'missing_variant' };
      if (!step.available) return { ok: false, reason: 'unavailable' };
      if (step.price !== q.totalCents) return { ok: false, reason: 'price_mismatch', expected: q.totalCents, found: step.price };
      return { ok: true, id: step.id, quantity: 1, lineCents: step.price };
    }

    return { ok: false, reason: 'no_catalog' };
  }

  /**
   * Choisit la ligne de panier de ce mur. Ordre : forfait minimum si le mur est petit ; sinon palier exact.
   * Au-delà de la surface maximale des paliers de la matière, le mur n'est plus vendu en ligne : le prix calculé
   * reste affiché à titre indicatif et la ligne est refusée (reason 'on_request', prix établi sur demande).
   * Le devis renvoyé est TOUJOURS celui de la ligne : le prix affiché est donc le prix payé.
   */
  function planCart(opts) {
    var base = {
      widthCm: opts.widthCm,
      heightCm: opts.heightCm,
      rateEurPerM2: opts.rateEurPerM2,
      minimumEur: opts.minimumEur,
      stripWidthCm: opts.stripWidthCm,
      extraCm2: opts.extraCm2,
    };
    var variants = opts.variants || [];
    var material = opts.material;
    var q = quote(Object.assign({}, base, { unitCm2: unitForMode('ladder', opts.stepDm2) }));
    if (!q) return { quote: null, line: { ok: false, reason: 'no_quote' }, mode: 'none' };
    var maxDm2 = maxStepDm2(variants, material);
    var billedDm2 = (q.billedUnits * q.unitCm2) / 100;
    if (!q.minimumApplied && maxDm2 > 0 && billedDm2 > maxDm2) {
      return { quote: q, line: { ok: false, reason: 'on_request', maxDm2: maxDm2 }, mode: 'on_request' };
    }
    var line = resolveCartLine(q, material, variants, 'ladder');
    return { quote: q, line: line, mode: q.minimumApplied ? 'minimum' : (line.ok ? 'ladder' : detectMode(variants, material)) };
  }

  /** Plus grande surface vendue en palier pour une matière (dm²), 0 si aucun palier. */
  function maxStepDm2(variants, material) {
    var max = 0;
    (variants || []).forEach(function (v) {
      var p = parseSku(v.sku);
      if (p && p.material === material && p.kind === 'step' && p.dm2 > max) max = p.dm2;
    });
    return max;
  }

  /**
   * Limites du cadrage. Le cadre (le mur) est placé sur l'image de l'œuvre.
   * fw = largeur du cadre en fraction de la largeur de l'image (1 = toute la largeur).
   * Hauteur du cadre en fraction : fh = fw × ratioImage × hauteurMur / largeurMur.
   * Zoom limité par la résolution : l'image entière ne peut pas être imprimée plus large
   * que largeurMaxCm, donc fw ≥ largeurMur / largeurMaxCm.
   * De même en hauteur (métachamp hauteur_max_cm) : fh ≥ hauteurMur / hauteurMaxCm,
   * soit fw ≥ largeurMur / (ratioImage × hauteurMaxCm). Un mur plus haut que hauteurMaxCm est infaisable
   * (tooTall) : le configurateur propose alors le demi-mur.
   */
  function frameLimits(opts) {
    var wallW = Number(opts.wallWidthCm);
    var wallH = Number(opts.wallHeightCm);
    var r = Number(opts.imageRatio); // largeur / hauteur de l'image
    var maxPrint = Number(opts.maxPrintWidthCm) || 0;
    var maxPrintH = Number(opts.maxPrintHeightCm) || 0;
    var floor = opts.minFrameFraction || 0.25;
    if (!(wallW > 0) || !(wallH > 0) || !(r > 0)) return null;
    var maxFw = Math.min(1, wallW / wallH / r);
    var minFw = maxPrint > 0 ? Math.max(wallW / maxPrint, Math.min(floor, maxFw)) : Math.min(floor, maxFw);
    if (maxPrintH > 0) minFw = Math.max(minFw, wallW / (r * maxPrintH));
    var feasible = minFw <= maxFw + 1e-9;
    return {
      tooWide: maxPrint > 0 && wallW > maxPrint + 1e-9,
      tooTall: maxPrintH > 0 && wallH > maxPrintH + 1e-9,
      maxFw: maxFw,
      minFw: feasible ? minFw : maxFw,
      feasible: feasible,
      maxZoom: feasible ? maxFw / minFw : 1,
      frameHeightFraction: function (fw) { return (fw * r * wallH) / wallW; },
    };
  }

  /**
   * Cadrage d'après la taille physique de l'aperçu (métachamps apercu_largeur_cm et apercu_hauteur_cm) :
   * l'image d'aperçu, imprimée à 100 %, couvre artW × artH cm. Le mur occupe donc wallW / artW de sa largeur
   * à l'échelle 1 (zoom maximal), et davantage si l'on imprime l'œuvre plus petite (zoom arrière).
   * - Motif non répété : mur plus large que artW ou plus haut que artH → infaisable (tooWide, tooTall).
   *   Zoom de 1 (l'œuvre la plus petite qui couvre le mur) à maxZoom (échelle 1, la limite de netteté).
   * - Motif répété : échelle fixe (pas de zoom), l'aperçu se répète en mosaïque ; fw et fh peuvent dépasser 1.
   * Le rapport hauteur/largeur utilisé est celui des dimensions physiques, pas celui du fichier.
   */
  function physicalFrame(opts) {
    var wallW = Number(opts.wallWidthCm);
    var wallH = Number(opts.wallHeightCm);
    var artW = Number(opts.artWidthCm);
    var artH = Number(opts.artHeightCm);
    if (!(wallW > 0) || !(wallH > 0) || !(artW > 0) || !(artH > 0)) return null;
    var r = artW / artH;
    var hfrac = function (fw) { return (fw * r * wallH) / wallW; };
    if (opts.repeat) {
      return {
        physical: true, repeat: true, feasible: true, tooWide: false, tooTall: false,
        maxFw: wallW / artW, minFw: wallW / artW, maxZoom: 1,
        frameHeightFraction: hfrac,
      };
    }
    var tooWide = wallW > artW + 1e-9;
    var tooTall = wallH > artH + 1e-9;
    var feasible = !tooWide && !tooTall;
    // s = échelle d'impression de l'œuvre (1 = taille physique de l'aperçu)
    var sMin = Math.max(wallW / artW, wallH / artH);
    var maxFw = feasible ? wallW / (artW * sMin) : Math.min(1, wallW / wallH / r);
    var maxZoom = feasible ? Math.min(opts.maxZoom || 4, 1 / sMin) : 1;
    return {
      physical: true, repeat: false, feasible: feasible, tooWide: tooWide, tooTall: tooTall,
      maxFw: maxFw, minFw: maxFw / maxZoom, maxZoom: maxZoom,
      frameHeightFraction: hfrac,
    };
  }

  /**
   * Limites de taille d'un motif, pour l'affichage avant la saisie et le choix de motifs compatibles.
   * Taille physique de l'aperçu si elle est connue (motif non répété), largeur sans limite propre pour un motif
   * répété (plafond général), sinon largeur et hauteur maximales du catalogue.
   */
  function motifLimits(m) {
    var g = m.globalMaxWidthCm || 1000;
    if (m.repeat) return { maxWidthCm: g, maxHeightCm: Number(m.maxHeightCm) || 0, repeat: true };
    var w = Number(m.artWidthCm) || Number(m.maxWidthCm) || 0;
    var h = Number(m.artHeightCm) || Number(m.maxHeightCm) || 0;
    if (Number(m.maxWidthCm) > 0 && w > Number(m.maxWidthCm)) w = Number(m.maxWidthCm);
    if (Number(m.maxHeightCm) > 0 && h > Number(m.maxHeightCm)) h = Number(m.maxHeightCm);
    return { maxWidthCm: w, maxHeightCm: h, repeat: false };
  }

  /** Ce motif couvre-t-il ce mur ? (0 = pas de limite connue) */
  function motifFits(m, wallW, wallH) {
    var l = motifLimits(m);
    return (!l.maxWidthCm || wallW <= l.maxWidthCm) && (!l.maxHeightCm || wallH <= l.maxHeightCm);
  }

  /**
   * Quelle dimension du mur dépasse le format maximal du motif (0 = pas de limite connue) :
   * null (aucune), 'width', 'height' ou 'both'.
   */
  function fitIssue(wallW, wallH, maxW, maxH) {
    var tooWide = Number(maxW) > 0 && Number(wallW) > Number(maxW);
    var tooTall = Number(maxH) > 0 && Number(wallH) > Number(maxH);
    if (tooWide && tooTall) return 'both';
    if (tooWide) return 'width';
    if (tooTall) return 'height';
    return null;
  }

  /**
   * Motifs à proposer pour un mur trop grand pour le motif affiché : ceux qui couvrent ce mur, d'abord ceux de la
   * première collection thématique du motif (opts.themes[0]), puis ceux d'une autre de ses collections thématiques,
   * puis les autres. Ordre du catalogue conservé dans chaque groupe. m.c = handles des collections du motif.
   */
  function compatibleMotifs(motifs, wallW, wallH, opts) {
    var o = opts || {};
    var themes = o.themes || [];
    var limit = o.limit || 3;
    var rank = function (m) {
      var c = m.c || [];
      if (themes.length && c.indexOf(themes[0]) !== -1) return 0;
      for (var i = 1; i < themes.length; i++) if (c.indexOf(themes[i]) !== -1) return 1;
      return 2;
    };
    return (motifs || [])
      .filter(function (m) { return m.id !== o.excludeId && motifFits(m, wallW, wallH); })
      .map(function (m, i) { return { m: m, r: rank(m), i: i }; })
      .sort(function (a, b) { return a.r - b.r || a.i - b.i; })
      .slice(0, limit)
      .map(function (x) { return x.m; });
  }

  /**
   * Variante du motif qui correspond au choix du client (matière INT/PRE/ADH, version light/dark).
   * variants = [{ id, m, t }] ; repli : même matière, puis null.
   */
  function motifVariant(variants, material, tint) {
    var list = variants || [];
    var exact = list.filter(function (v) { return v.m === material && v.t === tint; })[0];
    if (exact) return exact.id;
    var sameMat = list.filter(function (v) { return v.m === material; })[0];
    return sameMat ? sameMat.id : null;
  }

  /** Mesures gardées d'une fiche à l'autre (sessionStorage) : { width, height } si valides, sinon null. */
  function parseSavedWall(raw, limits) {
    if (!raw) return null;
    var d;
    try { d = JSON.parse(raw); } catch (e) { return null; }
    if (!d || typeof d !== 'object') return null;
    if (validateDimensions(d.w, d.h, limits).length) return null;
    return { width: toInt(d.w), height: toInt(d.h) };
  }

  /** Garde le cadre entier dans l'image. x et y = bord gauche et bord haut, en fractions. */
  function clampFrame(frame) {
    var fw = Math.min(1, Math.max(0, frame.fw));
    var fh = Math.min(1, Math.max(0, frame.fh));
    var x = Math.min(1 - fw, Math.max(0, frame.x));
    var y = Math.min(1 - fh, Math.max(0, frame.y));
    return { x: x, y: y, fw: fw, fh: fh };
  }

  /** Format monétaire français à partir de centimes : 33750 → « 337,50 € ». */
  function formatEuros(cents) {
    var n = Number(cents) / 100;
    var fixed = n.toFixed(2).split('.');
    var intPart = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return intPart + ',' + fixed[1] + ' €';
  }

  /**
   * Mur de référence d'une fiche (prix affiché à l'ouverture = prix de la variante = prix du flux Google).
   * largeur = min(300, largeur_max_cm), hauteur = min(250, hauteur_max_cm) (règle du lot catalogue, prix des
   * variantes alignés dessus). Même calcul côté Liquid (mp-main-product, mp-product-card).
   */
  function referenceWall(opts) {
    var w = Number(opts && opts.width) || 300;
    var h = Number(opts && opts.height) || 250;
    var mw = Math.floor(Number(opts && opts.maxPrintWidthCm) || 0);
    var mh = Math.floor(Number(opts && opts.maxPrintHeightCm) || 0);
    if (mw > 0 && mw < w) w = mw;
    if (mh > 0 && mh < h) h = mh;
    return { width: w, height: h };
  }

  /** Surface en m² avec virgule : 7.5 → « 7,5 m² », 7.56 → « 7,56 m² ». */
  function formatM2(m2) {
    var rounded = Math.round(m2 * 100) / 100;
    return String(rounded).replace('.', ',') + ' m²';
  }

  return {
    MATERIAL_CODES: MATERIAL_CODES,
    MAX_STRIP_CM: MAX_STRIP_CM,
    CUT_MARGIN_CM: CUT_MARGIN_CM,
    validateDimensions: validateDimensions,
    isIntegerInput: isIntegerInput,
    equalStripCount: equalStripCount,
    equalStrips: equalStrips,
    stripPlan: stripPlan,
    quote: quote,
    minimumSurfaceM2: minimumSurfaceM2,
    parseSku: parseSku,
    detectMode: detectMode,
    unitForMode: unitForMode,
    resolveCartLine: resolveCartLine,
    planCart: planCart,
    maxStepDm2: maxStepDm2,
    frameLimits: frameLimits,
    physicalFrame: physicalFrame,
    motifLimits: motifLimits,
    motifFits: motifFits,
    fitIssue: fitIssue,
    compatibleMotifs: compatibleMotifs,
    motifVariant: motifVariant,
    parseSavedWall: parseSavedWall,
    referenceWall: referenceWall,
    clampFrame: clampFrame,
    formatEuros: formatEuros,
    formatM2: formatM2,
  };
});
